'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Sparkles, Zap, ArrowRight, ArrowDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { urlEncoderContent } from '../../data/url-encoder-content';
import Link from 'next/link';

export default function UrlEncodeTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [activeTab, setActiveTab] = useState('encode');
    const [isCopied, setIsCopied] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if (input.trim() === '') {
            setOutput('');
            return;
        }

        try {
            if (activeTab === 'encode') {
                setOutput(encodeURIComponent(input));
            } else {
                setOutput(decodeURIComponent(input));
            }
        } catch (error) {
            setOutput('Error: Invalid input for decoding');
        }
    }, [input, activeTab]);

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: "Copied to clipboard!",
            description: "The text has been copied to your clipboard.",
            variant: "default",
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
    };

    const handleSwitch = () => {
        setInput(output);
        setActiveTab(prev => prev === 'encode' ? 'decode' : 'encode');
    };

    const sampleText = 'Hello World! How are you?';
    const sampleEncoded = 'https%3A%2F%2Fexample.com%2Fpage%3Fquery%3Dsearch%26id%3D123';

    const loadSample = () => {
        setInput(activeTab === 'encode' ? sampleText : sampleEncoded);
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="URL Encoder/Decoder">
                Encode or decode URLs and URL-encoded strings with our free online URL encoder. Perfect for web developers and for handling special characters in URLs.
            </ToolPageIntro>
            </div>

            <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full space-y-8"
            >
                <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 h-12 bg-muted/50 backdrop-blur-sm">
                    <TabsTrigger
                        value="encode"
                        className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary relative group"
                    >
                        <Zap className="h-4 w-4 mr-2 text-yellow-500 group-data-[state=active]:animate-pulse" />
                        Encode
                    </TabsTrigger>
                    <TabsTrigger
                        value="decode"
                        className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary relative group"
                    >
                        <Zap className="h-4 w-4 mr-2 text-blue-500 group-data-[state=active]:animate-pulse" />
                        Decode
                    </TabsTrigger>
                </TabsList>

                <div className="grid gap-6">
                    <Card className="border-0 shadow-lg overflow-hidden">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        {activeTab === 'encode' ? 'Original Text' : 'Encoded URL'}
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {activeTab === 'encode'
                                            ? 'Enter your text to encode it for URLs'
                                            : 'Paste an encoded URL to decode it'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadSample}
                                        className="gap-1.5"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            <Textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={
                                    activeTab === 'encode'
                                        ? 'e.g., Hello World! How are you?'
                                        : 'e.g., https%3A%2F%2Fexample.com%2Fpage%3Fid%3D123'
                                }
                                className="min-h-[180px] font-mono text-base border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none"
                                spellCheck={false}
                            />
                        </CardContent>
                    </Card>

                    <div className="flex justify-center">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleSwitch}
                            className="rounded-full w-14 h-14 shadow-lg hover:shadow-primary/20 hover:bg-primary/5 transition-all duration-300"
                            title="Switch between encode/decode"
                        >
                            <RefreshCw className="h-6 w-6 text-primary" />
                        </Button>
                    </div>

                    <Card className="border-0 shadow-lg overflow-hidden">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        {activeTab === 'encode' ? 'Encoded Result' : 'Decoded Result'}
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {activeTab === 'encode'
                                            ? 'Your URL-safe encoded text'
                                            : 'Your decoded text'}
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!output}
                                    className={cn(
                                        "gap-1.5 transition-all",
                                        isCopied
                                            ? "bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20"
                                            : "hover:bg-primary/5"
                                    )}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="relative">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        "min-h-[180px] font-mono text-base border-0 bg-muted/20",
                                        "focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none"
                                    )}
                                    placeholder={
                                        activeTab === 'encode'
                                            ? 'Encoded result will appear here...'
                                            : 'Decoded result will appear here...'
                                    }
                                />
                                {!output && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm">
                                            {activeTab === 'encode'
                                                ? 'Your encoded URL will appear here'
                                                : 'Your decoded text will appear here'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </Tabs>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={urlEncoderContent.title}
                    description={urlEncoderContent.description}
                    content={urlEncoderContent.content}
                    faqs={urlEncoderContent.faqs}
                    relatedTools={urlEncoderContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}